/**
 * Author portraits, keyed by byline.
 *
 * Every byline on the site used to point at whatever image happened to sit
 * beside it in its own fixture — and those were ARTICLE photos, not headshots:
 * 620×325, 1080×615 and 201×251 landscapes, `object-cover`-cropped into a
 * 24–62px circle. That crop takes the centre of a landscape frame, which is
 * rarely the face, and the 201×251 ones had almost no pixels left to work with.
 * QA: "the author images used in the website are very blurry."
 *
 * These replacements are square 384×384 portraits, so the circle crop is
 * lossless and the same writer wears the same face everywhere.
 *
 * Filenames are the writer's name. Non-ASCII paths are fine — Next serves them
 * from `public/` and `next/image` percent-encodes them — but note the file must
 * exist BEFORE the server boots or the request 500s rather than 404s.
 */
/**
 * Byline spellings that name the SAME writer.
 *
 * 杜比 (10 fixtures, including the nav and 我的追蹤) and 杜比Sir (the supplied
 * 投資搏擊 copy) are one person — so without this they drew two different
 * portraits and two different author pages. QA: "杜比 is the same person as
 * 杜比Sir". The canonical form is the one the rest of the site already uses.
 */
const AUTHOR_ALIASES: Record<string, string> = {
  "杜比Sir": "杜比",
};

/** The canonical spelling of a byline. */
export function canonicalAuthor(name: string): string {
  const key = name.replace(/[​-‍﻿]/g, "").trim();
  return AUTHOR_ALIASES[key] ?? key;
}

const AUTHOR_PORTRAITS: Record<string, string> = {
  丁學良: "/images/authors/丁學良.png",
  信析: "/images/authors/信析.png",
  呂梓毅: "/images/authors/呂梓毅.png",
  張炳良: "/images/authors/張炳良.png",
  曾德成: "/images/authors/曾德成.png",
  // File is named for the alias; the key is the canonical byline.
  杜比: "/images/authors/杜比Sir.png",
  王緝憲: "/images/authors/王緝憲.png",
  社評: "/images/authors/社評.png",
  程介明: "/images/authors/程介明.png",
  翁靜晶: "/images/authors/翁靜晶.png",
  謝嫣薇: "/images/authors/謝嫣薇.png",
  高天佑: "/images/authors/高天佑.png",
  黃裕舜: "/images/authors/黃裕舜.png",
};

/**
 * The portrait for a byline, or `fallback` when none has been supplied yet.
 *
 * Only writers with a real portrait are listed above. Everyone else keeps the
 * fixture's existing image rather than being given someone else's face, so
 * adding a portrait later is a one-line change here and nothing else.
 */
export function authorAvatar(name: string, fallback: string): string {
  return AUTHOR_PORTRAITS[canonicalAuthor(name)] ?? fallback;
}

/** Whether a real portrait exists for this byline. */
export function hasAuthorPortrait(name: string): boolean {
  return canonicalAuthor(name) in AUTHOR_PORTRAITS;
}

/** The bylines a portrait has been supplied for — used by the audit script. */
export const AUTHORS_WITH_PORTRAITS = Object.keys(AUTHOR_PORTRAITS);
